import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InputMaybe,
  UpdateImpactMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateImpactDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface UpdateImpactInput {
  Id: string;
  /**
   * Optimistic-concurrency token (COI-622) — the impact row's last-seen
   * `ModifiedAtTimestamp`. The legacy `updateImpactApi` input has no such field,
   * so it is stripped on the Apollo leg and only the tRPC path locks on it.
   */
  OriginalTimestamp?: string | null;
  Name: string;
  Rationale?: InputMaybe<string>;
  RatingGuidance?: InputMaybe<string>;
  LikelihoodAppetite?: InputMaybe<number>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
}

export const useUpdateImpact = createMutationHook({
  name: 'updateImpact',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.impact.update.mutationOptions({ onSuccess }),
  mapInput: (input: UpdateImpactInput) => input,
  invalidate: (trpc) => [
    trpc.frontend.impact.impacts.queryKey(),
    trpc.frontend.impact.impactById.queryKey(),
    trpc.frontend.impact.impactList.queryKey(),
    // Impact updates may affect rating views
    trpc.frontend.impactRating.impactRatingsWithAppetites.queryKey(),
  ],
  mapResponse: (): UpdateImpactMutation => ({
    updateImpactApi: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateImpactDocument,
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...input
  }: UpdateImpactInput) => ({
    object: input,
  }),
  evictFields: ['impact', 'impact_rating'],
});
